/* eslint-disable react/prop-types */
import React, { useEffect, useCallback } from 'react';
import { FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import * as turf from '@turf/turf';
import L from 'leaflet';
import { isEmpty } from 'lodash';

const MapComponent = ({
  uploadedGeoJSONLayer,
  isMapEditable = true,
  featureGroupRef,
  hasLayer,
  setHasLayer,
  setPolygonData,
  setAreaHectares,
  setAreaAcres,
  setPerimeter
}) => {
  const map = useMap();

  // Helper function to calculate and update area
  const updateAreaCalculations = useCallback(
    (layer, geoJSON) => {
      try {
        if (!geoJSON) return;

        // Calculate area
        const area = turf.area(geoJSON);
        const areaHectares = area / 10000;
        const areaAcres = area / 4046.8564224;

        // Robustly get perimeter
        let perimeter = 0;
        try {
          // Get the primary geometry for perimeter calculation
          let geometry = geoJSON.geometry;
          if (geoJSON.type === 'FeatureCollection' && geoJSON.features?.[0]) {
            geometry = geoJSON.features[0].geometry;
          } else if (geoJSON.type === 'Feature') {
            geometry = geoJSON.geometry;
          }

          if (geometry?.type === 'Polygon' && geometry.coordinates?.[0]) {
            const line = turf.lineString(geometry.coordinates[0]);
            perimeter = turf.length(line, { units: 'meters' });
          }
        } catch (perimError) {
          console.error('Error calculating perimeter:', perimError);
        }

        // Update the tooltip
        if (layer && layer.bindTooltip) {
          layer
            .bindTooltip(
              `Area: ${areaHectares.toFixed(2)} ha / ${areaAcres.toFixed(
                2
              )} acres`
            )
            .openTooltip();
        }

        // Update state
        setPerimeter(perimeter);
        setAreaHectares(areaHectares);
        setAreaAcres(areaAcres);
        setPolygonData(geoJSON);
      } catch (error) {
        console.error('Error calculating area:', error);
      }
    },
    [setAreaHectares, setAreaAcres, setPolygonData, setPerimeter]
  );

  useEffect(() => {
    if (
      uploadedGeoJSONLayer &&
      !isEmpty(uploadedGeoJSONLayer) &&
      featureGroupRef.current
    ) {
      try {
        featureGroupRef.current.clearLayers();

        const leafletLayer = L.geoJSON(uploadedGeoJSONLayer, {
          interactive: true,
          bubblingMouseEvents: true,
          pmIgnore: false
        }).setStyle({
          color: '#ff0000',
          fillColor: '#ff0000',
          fillOpacity: 0.2
        });

        if (!isMapEditable) {
          featureGroupRef.current.addLayer(leafletLayer);
          setHasLayer(true);
        } else {
          const polygonLayer = Object.values(leafletLayer._layers)[0];
          if (polygonLayer) {
            polygonLayer.options.editing = true;
            featureGroupRef.current.addLayer(polygonLayer);
            setHasLayer(true);
          }
        }

        // Fit bounds to the uploaded layer
        if (map && !leafletLayer.getBounds().isEmpty()) {
          map.fitBounds(leafletLayer.getBounds(), {
            padding: [20, 20],
            maxZoom: 18
          });
        }

        // Update area calculations for the first polygon or the whole set
        const firstLayer = Object.values(leafletLayer._layers)[0];
        if (firstLayer) {
          updateAreaCalculations(firstLayer, uploadedGeoJSONLayer);
        }
      } catch (error) {
        console.error('Error adding layer:', error);
      }
    }
  }, [
    uploadedGeoJSONLayer,
    featureGroupRef,
    updateAreaCalculations,
    setHasLayer
  ]);

  const _onCreate = e => {
    console.log(e, 'created');
    const { layer } = e;
    if (featureGroupRef.current) {
      // Clear existing layers before adding new one
      featureGroupRef.current.clearLayers();
      featureGroupRef.current.addLayer(layer);

      setHasLayer(true);
      const geoJSON = layer.toGeoJSON();
      if (geoJSON.geometry.type === 'Polygon') {
        updateAreaCalculations(layer, geoJSON);
      }
    }
  };

  const _onEdited = e => {
    try {
      const {
        layers: { _layers }
      } = e;
      // Get the edited layer
      const layer = Object.values(_layers)[0];
      if (layer) {
        const geoJSON = layer.toGeoJSON();
        if (geoJSON.geometry.type === 'Polygon') {
          // Force update the feature group
          if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
            featureGroupRef.current.addLayer(layer);
          }
          updateAreaCalculations(layer, geoJSON);
        }
      }
    } catch (error) {
      console.error('Error in edit handler:', error);
    }
  };

  const _onDeleted = () => {
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    setHasLayer(false);
    setPolygonData(null);
    setAreaHectares(0);
    setAreaAcres(0);
    setPerimeter(0);
  };

  return (
    <FeatureGroup ref={featureGroupRef}>
      {isMapEditable && (
        <EditControl
          position="bottomright"
          onCreated={_onCreate}
          onEdited={_onEdited}
          onDeleted={_onDeleted}
          draw={{
            circlemarker: false,
            marker: false,
            polyline: false,
            rectangle: false,
            circle: false,
            polygon: hasLayer
              ? false
              : {
                  showArea: true,
                  allowIntersection: false,
                  drawError: {
                    color: '#e1e100',
                    message: "<strong>Oh snap!<strong> you can't draw that!"
                  },
                  shapeOptions: {
                    color: '#ff0000',
                    fillColor: '#ff0000',
                    fillOpacity: 0.2
                  }
                }
          }}
          edit={{
            featureGroup: featureGroupRef.current,
            remove: isMapEditable,
            edit: isMapEditable,
            editMode: {
              selectedPathOptions: {
                maintainColor: true,
                moveMarkers: true,
                color: '#ff0000'
              }
            }
          }}
        />
      )}
    </FeatureGroup>
  );
};

export default MapComponent;
